(() => {
  'use strict';

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const liveRegion = () => qs('#B2SLiveRegion');

  function announce(message) {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  }

  function lockBody(lock = true) {
    document.body.classList.toggle('is-locked', lock);
  }

  function openPanel(panel, focusTarget) {
    if (!panel) return;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    lockBody(true);
    window.setTimeout(() => {
      const focusable = focusTarget || qs('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', panel);
      focusable?.focus();
    }, 20);
  }

  function closePanel(panel, returnFocus) {
    if (!panel) return;
    panel.hidden = true;
    panel.setAttribute('aria-hidden', 'true');
    lockBody(false);
    returnFocus?.focus();
  }

  function initAnnouncement(scope = document) {
    qsa('[data-announcement-close]', scope).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const bar = button.closest('[data-announcement]');
        if (bar) bar.hidden = true;
        try { sessionStorage.setItem('b2sAnnouncementClosed', '1'); } catch (_) {}
      });
    });
    if (sessionStorage.getItem('b2sAnnouncementClosed') === '1') {
      qsa('[data-announcement]', scope).forEach((bar) => { bar.hidden = true; });
    }
  }

  function initMobileNav(scope = document) {
    const panel = qs('[data-mobile-nav]', scope) || qs('[data-mobile-nav]');
    const openers = qsa('[data-mobile-nav-open]', scope);
    const closers = panel ? qsa('[data-mobile-nav-close]', panel) : [];
    openers.forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        openPanel(panel, qs('[data-mobile-nav-close]', panel));
        button.setAttribute('aria-expanded', 'true');
        panel.dataset.returnId = button.id || '';
      });
    });
    closers.forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const opener = openers[0];
        closePanel(panel, opener);
        opener?.setAttribute('aria-expanded', 'false');
      });
    });
  }

  const safeTitles = {
    '/products/27': 'Card NFC personalizzata – configurazione da verificare',
    '/products/genioreview-personalizzato-6-card-tripadvisor-6-card-google': 'Kit 12 card NFC per link e feedback',
    '/products/grcard': 'Card NFC per feedback e recensioni',
    '/products/menu-genioreview': 'Card NFC per menu digitale',
    '/products/pagina-facebook-genioreview': 'Card NFC per pagina social',
    '/products/profilo-instagram-genioreview': 'Card NFC per profilo social',
    '/products/profilo-linkedin-genioreview': 'Card NFC per profilo professionale',
    '/products/recensione-airbnb-genioreview': 'Card NFC per pagina della struttura',
    '/products/genioreview-recensioni-tripadvisor': 'Card NFC per feedback ospitalità e ristorazione',
    '/products/square-google-genioreview': 'Espositore NFC e QR per feedback',
    '/products/visita-il-sito': 'Card NFC per sito web',
    '/products/genioreview-whatsapp': 'Card NFC per chat e contatti',
    '/products/wi-fi-automatico-genioreview': 'Card NFC e QR per accesso Wi‑Fi'
  };

  function productPathFromUrl(url) {
    try {
      const path = new URL(url, window.location.origin).pathname.replace(/\/$/, '');
      const match = path.match(/\/products\/[^/]+$/);
      return match ? match[0] : path;
    } catch (_) {
      return '';
    }
  }

  function safeTitleForUrl(url, fallback) {
    return safeTitles[productPathFromUrl(url)] || fallback;
  }

  function isMappedProductUrl(url) {
    return Boolean(safeTitles[productPathFromUrl(url)]);
  }

  function initSearch(scope = document) {
    const overlay = qs('[data-search-overlay]');
    const openers = qsa('[data-search-open]', scope);
    const closers = overlay ? qsa('[data-search-close]', overlay) : [];
    let returnFocus = null;
    openers.forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        returnFocus = button;
        openPanel(overlay, qs('[data-predictive-search-input]', overlay));
      });
    });
    closers.forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => closePanel(overlay, returnFocus));
    });

    const input = qs('[data-predictive-search-input]', overlay || scope);
    const results = qs('[data-predictive-results]', overlay || scope);
    if (!input || !results || input.dataset.bound) return;
    input.dataset.bound = 'true';
    let controller;
    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const term = input.value.trim();
      if (term.length < 2) { results.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        results.innerHTML = '<p>Ricerca in corso…</p>';
        try {
          const url = `${window.B2STheme.searchSuggestUrl}.json?q=${encodeURIComponent(term)}&resources[type]=product,page,article&resources[limit]=6`;
          const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Search failed');
          const data = await response.json();
          const queries = data.resources?.results || {};
          const items = [...(queries.products || []), ...(queries.pages || []), ...(queries.articles || [])].slice(0, 6);
          if (!items.length) {
            results.innerHTML = '<p>Nessun risultato. Prova con un termine diverso.</p>';
            return;
          }
          results.innerHTML = `<div class="predictive-results__list">${items.map((item) => {
            const isProduct = item.url?.includes('/products/');
            const title = isProduct ? safeTitleForUrl(item.url, item.title) : item.title;
            const hideLegacyImage = isProduct && window.B2STheme.safeVisuals && isMappedProductUrl(item.url);
            const image = hideLegacyImage ? '' : (item.featured_image?.url || item.image || '');
            const media = image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : '<span class="predictive-results__safe">NFC</span>';
            return `<a class="predictive-results__item" href="${escapeHtml(item.url)}">${media}<span><strong>${escapeHtml(title)}</strong><small>${escapeHtml(item.price || item.type || '')}</small></span></a>`;
          }).join('')}</div>`;
        } catch (error) {
          if (error.name !== 'AbortError') results.innerHTML = '<p>Ricerca temporaneamente non disponibile.</p>';
        }
      }, 220);
    });
  }

  function escapeHtml(value = '') {
    return String(value).replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
  }

  function initQuantity(scope = document) {
    qsa('[data-quantity]', scope).forEach((component) => {
      if (component.dataset.bound) return;
      component.dataset.bound = 'true';
      const input = qs('[data-quantity-input]', component);
      qs('[data-quantity-minus]', component)?.addEventListener('click', () => {
        const min = Number(input.min || 1);
        input.value = Math.max(min, Number(input.value || 1) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      qs('[data-quantity-plus]', component)?.addEventListener('click', () => {
        const max = input.max ? Number(input.max) : Infinity;
        input.value = Math.min(max, Number(input.value || 1) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function formatMoney(cents, format = window.B2STheme.moneyFormat || '€{{amount}}') {
    const amount = Number(cents || 0) / 100;
    const formatted = new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return format
      .replace(/{{\s*amount_with_comma_separator\s*}}/, formatted)
      .replace(/{{\s*amount\s*}}/, formatted)
      .replace(/{{\s*amount_no_decimals\s*}}/, Math.round(amount).toString())
      .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/, Math.round(amount).toString());
  }

  function initProduct(scope = document) {
    qsa('[data-product-root]', scope).forEach((root) => {
      if (root.dataset.bound) return;
      root.dataset.bound = 'true';
      const json = qs('[data-product-json]', root);
      if (!json) return;
      let variants = [];
      try { variants = JSON.parse(json.textContent); } catch (_) { return; }
      const selects = qsa('[data-option-select]', root);
      const variantInput = qs('[data-variant-id]', root);
      const addButton = qs('[data-add-to-cart]', root);
      const addText = qs('[data-add-to-cart-text]', root);
      const price = qs('[data-price]', root);
      const compare = qs('[data-compare-price]', root);
      const status = qs('[data-product-status]', root);
      const blocked = root.dataset.productBlocked === 'true';
      const storeNotReady = root.dataset.storeNotReady === 'true';

      const update = () => {
        const options = selects.map((select) => select.value);
        const variant = variants.find((item) => item.options.every((value, index) => value === options[index])) || variants[0];
        if (!variant) return;
        if (variantInput) variantInput.value = variant.id;
        if (price) price.innerHTML = formatMoney(variant.price);
        if (compare) {
          const showCompare = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
          compare.hidden = !showCompare;
          compare.innerHTML = showCompare ? formatMoney(variant.compare_at_price) : '';
        }
        const canBuy = variant.available && !blocked;
        if (addButton) addButton.disabled = !canBuy;
        if (addText) addText.textContent = storeNotReady ? 'Negozio in configurazione' : (blocked ? 'Completa la scheda prima della vendita' : (variant.available ? window.B2STheme.strings.addToCart : window.B2STheme.strings.soldOut));
        if (status) {
          status.textContent = storeNotReady ? 'Vendita temporaneamente non disponibile finché i dati obbligatori non sono stati verificati.' : (blocked ? 'Prodotto non acquistabile finché la scheda non è completa.' : (variant.available ? 'Disponibile' : 'Non disponibile'));
          status.className = `product-status ${canBuy ? 'product-status--available' : 'product-status--unavailable'}`;
        }
        if (variant.featured_media?.id) {
          const thumb = qs(`[data-media-target="${variant.featured_media.id}"]`, root);
          thumb?.click();
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      };
      selects.forEach((select) => select.addEventListener('change', update));
      update();
    });

    qsa('[data-media-target]', scope).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const gallery = button.closest('[data-product-gallery]');
        if (!gallery) return;
        qsa('[data-media-target]', gallery).forEach((item) => item.classList.toggle('is-active', item === button));
        qsa('[data-media-id]', gallery).forEach((media) => media.classList.toggle('is-active', media.dataset.mediaId === button.dataset.mediaTarget));
      });
    });
  }

  async function refreshCartDrawer(open = true) {
    const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Unable to refresh cart');
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = qs('[data-cart-drawer]', doc);
    const current = qs('[data-cart-drawer]');
    if (fresh && current) current.replaceWith(fresh);
    initCartDrawer(document);
    initQuantity(document);
    const cartData = await fetch('/cart.js').then((r) => r.json());
    qsa('[data-cart-count]').forEach((node) => {
      node.textContent = cartData.item_count;
      node.hidden = cartData.item_count === 0;
    });
    if (open) openCartDrawer();
  }

  function openCartDrawer(returnFocus) {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    if (returnFocus) drawer._returnFocus = returnFocus;
    openPanel(drawer, qs('[data-cart-drawer-close]', drawer));
  }

  function closeCartDrawer() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    closePanel(drawer, drawer._returnFocus);
  }

  function initCartDrawer(scope = document) {
    qsa('[data-cart-drawer-open]', scope).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', (event) => {
        if (window.B2STheme.ajaxCart) {
          event.preventDefault();
          openCartDrawer(button);
        }
      });
    });
    const drawer = qs('[data-cart-drawer]', scope) || qs('[data-cart-drawer]');
    if (!drawer) return;
    qsa('[data-cart-drawer-close]', drawer).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', closeCartDrawer);
    });
    qsa('[data-cart-line-input]', drawer).forEach((input) => {
      if (input.dataset.bound) return;
      input.dataset.bound = 'true';
      input.addEventListener('change', async () => {
        input.disabled = true;
        try {
          await fetch(window.B2STheme.cartChangeUrl + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: input.dataset.key, quantity: Number(input.value) })
          });
          await refreshCartDrawer(true);
        } catch (_) {
          announce(window.B2STheme.strings.error);
          input.disabled = false;
        }
      });
    });
    qsa('[data-cart-remove]', drawer).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        try {
          await fetch(window.B2STheme.cartChangeUrl + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: button.dataset.key, quantity: 0 })
          });
          await refreshCartDrawer(true);
        } catch (_) { announce(window.B2STheme.strings.error); }
      });
    });
  }

  function initProductForms(scope = document) {
    qsa('form[data-product-form]', scope).forEach((form) => {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', async (event) => {
        if (!window.B2STheme.ajaxCart) return;
        event.preventDefault();
        const button = qs('[data-add-to-cart]', form);
        if (button?.disabled) return;
        button?.classList.add('is-loading');
        button?.setAttribute('aria-busy', 'true');
        const errorNode = qs('[data-product-form-error]', form);
        if (errorNode) { errorNode.hidden = true; errorNode.textContent = ''; }
        try {
          const response = await fetch(window.B2STheme.cartAddUrl + '.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || window.B2STheme.strings.error);
          announce('Prodotto aggiunto al carrello.');
          await refreshCartDrawer(true);
        } catch (error) {
          if (errorNode) { errorNode.textContent = error.message; errorNode.hidden = false; }
          else announce(error.message);
        } finally {
          button?.classList.remove('is-loading');
          button?.removeAttribute('aria-busy');
        }
      });
    });
  }

  function initFilters(scope = document) {
    qsa('[data-sort-select]', scope).forEach((select) => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
    const mobile = qs('[data-mobile-facets]');
    qsa('[data-mobile-facets-open]', scope).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => openPanel(mobile, qs('[data-mobile-facets-close]', mobile)));
    });
    qsa('[data-mobile-facets-close]', mobile || scope).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => closePanel(mobile));
    });
  }

  function initProductRecommendations(scope = document) {
    qsa('[data-product-recommendations]', scope).forEach(async (element) => {
      if (element.dataset.loaded === 'true') return;
      const url = element.dataset.url;
      if (!url) return;
      try {
        const html = await fetch(url).then((response) => response.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = qs('[data-product-recommendations]', doc);
        if (fresh?.innerHTML.trim()) {
          element.innerHTML = fresh.innerHTML;
          element.dataset.loaded = 'true';
          initProductForms(element);
        } else {
          element.closest('.section')?.remove();
        }
      } catch (_) {
        element.closest('.section')?.remove();
      }
    });
  }

  function initAddressToggles(scope = document) {
    qsa('[data-address-toggle]', scope).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const target = document.getElementById(button.dataset.addressToggle);
        if (!target) return;
        target.hidden = !target.hidden;
        if (!target.hidden) qs('input,select,textarea', target)?.focus();
      });
    });
  }

  function initCookiePreferences(scope = document) {
    qsa('[data-cookie-preferences]', scope).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        if (window.Shopify?.customerPrivacy?.showPreferences) {
          window.Shopify.customerPrivacy.showPreferences();
        } else {
          announce('Le preferenze cookie sono disponibili tramite il banner privacy configurato in Shopify.');
        }
      });
    });
  }

  function initAll(scope = document) {
    initAnnouncement(scope);
    initMobileNav(scope);
    initSearch(scope);
    initQuantity(scope);
    initProduct(scope);
    initProductForms(scope);
    initCartDrawer(scope);
    initFilters(scope);
    initProductRecommendations(scope);
    initAddressToggles(scope);
    initCookiePreferences(scope);
  }

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (!qs('[data-cart-drawer][hidden]')) closeCartDrawer();
    const search = qs('[data-search-overlay]:not([hidden])');
    if (search) closePanel(search);
    const nav = qs('[data-mobile-nav]:not([hidden])');
    if (nav) closePanel(nav);
    const filters = qs('[data-mobile-facets]:not([hidden])');
    if (filters) closePanel(filters);
  });
})();
